import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAddJobPart } from '@/hooks/useJobParts';
import { usePartsCatalog } from '@/hooks/useReferenceData';
import { usePartsMarkup, useDefaultPartSource } from '@/hooks/usePartsMarkup';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { effectiveSellPrice } from '@/utils/partsPricing';

const partSchema = z.object({
  part_name: z.string().min(1, 'Part name is required'),
  part_cost: z.string().min(1, 'Cost is required'),
  sell_price: z.string().min(1, 'Sell price is required'),
  quantity: z.string().min(1, 'Quantity is required'),
  notes: z.string().optional(),
  part_source: z.enum(['company_stock', 'tech_personal']),
});

type PartFormData = z.infer<typeof partSchema>;

interface AddJobPartDialogProps {
  jobId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddJobPartDialog({ jobId, open, onOpenChange }: AddJobPartDialogProps) {
  const { data: catalogParts = [] } = usePartsCatalog();
  const addPart = useAddJobPart();
  const markupPct = usePartsMarkup();
  const defaultSource = useDefaultPartSource();
  const { formatMoney } = useCompanyLocale();
  // Once the user types a sell price by hand we stop recomputing it from cost + markup.
  const sellTouched = useRef(false);

  const blank = { part_name: '', part_cost: '', sell_price: '', quantity: '1', notes: '', part_source: defaultSource };
  const form = useForm<PartFormData>({
    resolver: zodResolver(partSchema),
    defaultValues: blank,
  });

  const applyMarkup = (cost: string) => {
    if (sellTouched.current) return;
    const c = parseFloat(cost);
    form.setValue('sell_price', isNaN(c) ? '' : effectiveSellPrice(c, null, markupPct).toFixed(2));
  };

  const handleSubmit = (data: PartFormData) => {
    addPart.mutate({
      job_id: jobId,
      part_name: data.part_name,
      part_cost: parseFloat(data.part_cost),
      sell_price: parseFloat(data.sell_price),
      quantity: parseInt(data.quantity),
      notes: data.notes || undefined,
      part_source: data.part_source,
    }, {
      onSuccess: () => { form.reset(blank); sellTouched.current = false; onOpenChange(false); },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Part to Job</DialogTitle>
          <DialogDescription className="sr-only">Record a part used on this job, including source, cost, sell price and quantity.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {catalogParts.length > 0 && (
              <div>
                <label className="text-sm font-medium">Select from Catalog</label>
                <Select onValueChange={(id) => {
                  const part = catalogParts.find(p => p.id === id);
                  if (part) {
                    form.setValue('part_name', part.name);
                    if (part.default_cost) form.setValue('part_cost', part.default_cost.toString());
                    sellTouched.current = false;
                    form.setValue('sell_price', effectiveSellPrice(part.default_cost, part.default_sell_price, markupPct).toFixed(2));
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="Choose a part" /></SelectTrigger>
                  <SelectContent>
                    {catalogParts.map(part => (
                      <SelectItem key={part.id} value={part.id}>
                        {part.name} ({formatMoney(effectiveSellPrice(part.default_cost, part.default_sell_price, markupPct))})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <FormField control={form.control} name="part_name" render={({ field }) => (
              <FormItem><FormLabel>Part Name *</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
            )} />
            <div className="grid grid-cols-3 gap-4">
              <FormField control={form.control} name="part_cost" render={({ field }) => (
                <FormItem>
                  <FormLabel>Internal cost *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      onChange={(e) => { field.onChange(e); applyMarkup(e.target.value); }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="sell_price" render={({ field }) => (
                <FormItem>
                  <FormLabel>Sell price *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      onChange={(e) => { sellTouched.current = true; field.onChange(e); }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="quantity" render={({ field }) => (
                <FormItem><FormLabel>Quantity *</FormLabel><FormControl><Input {...field} type="number" min="1" /></FormControl><FormMessage /></FormItem>
              )} />
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Sell price defaults to cost + {markupPct}% markup. Only the sell price appears on the invoice.
            </p>
            <FormField control={form.control} name="part_source" render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Who paid for this part?</FormLabel>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-col space-y-1">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="tech_personal" id="tech_personal" />
                      <Label htmlFor="tech_personal" className="font-normal cursor-pointer">🔧 Tech Personal (reimbursement)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="company_stock" id="company_stock" />
                      <Label htmlFor="company_stock" className="font-normal cursor-pointer">📦 Company Stock</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem><FormLabel>Notes</FormLabel><FormControl><Textarea {...field} rows={3} /></FormControl><FormMessage /></FormItem>
            )} />
            <Button type="submit" className="w-full">Add Part</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
